import React, { RefObject } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import { slugify } from 'utils/helpers';
import { MessageBox } from 'components/_new/MessageBox';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Link } from 'components/_new/Link';
import { Box, BoxSection } from 'components/_new/Box';
import { usePanel } from 'contexts/panelContext';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { ArticlePresenter } from './ArticlePresenter';
import { useArticleRead } from './useArticleRead';

type KnowledgeBaseArticleProps = { scrollContainerRef?: RefObject<HTMLDivElement>; disableRating?: boolean };

export const KnowledgeBaseArticle = ({ scrollContainerRef, disableRating = false }: KnowledgeBaseArticleProps) => {
	const { t } = useTranslation();
	const { organizationName, programName, type: panelType } = useParams();

	const { panelLinkPrefix } = usePanel();
	const { articleQuery, goToHome, knowledgeBaseLinkPrefix } = useKnowledgeBase();

	const { readed } = useArticleRead({
		enabled: Boolean(!articleQuery.isError && articleQuery.isSuccess && articleQuery?.data.article),
		scrollContainerRef,
		articleData: articleQuery?.data,
	});

	if (articleQuery.isLoading) {
		return (
			<Box variant="white-bordered">
				<BoxSection>
					<Skeleton width="60%" height="2.5rem" className="mb-4" />
					<Skeleton width="100%" height="1.5rem" className="mb-2" />
					<Skeleton width="100%" height="1.5rem" className="mb-2" />
					<Skeleton width="100%" height="1.5rem" className="mb-2" />
					<Skeleton width="80%" height="1.5rem" className="mb-4" />
					<Skeleton width="100%" height="300px" />
				</BoxSection>
			</Box>
		);
	}

	if (articleQuery.isError) {
		return (
			<ErrorContainer
				errorMessage={t('knowledgeBase.errorOccurred')}
				action={() => {
					articleQuery.refetch();
				}}
				error={articleQuery.error}
			/>
		);
	}

	if (articleQuery.isSuccess && articleQuery?.data) {
		if (!articleQuery.data.articleContent) {
			if (!articleQuery.data.alternates.length) {
				return (
					<ErrorContainer
						errorMessage={t('knowledgeBase.errorNotFound')}
						actionName={t('knowledgeBase.returnToMainPage')}
						action={() => {
							goToHome();
						}}
					/>
				);
			}

			if (articleQuery.data.alternates.length > 0) {
				return (
					<>
						<MessageBox message={t('knowledgeBase.articleIsNotAvailableInYourLanguage')} />
						<div>
							<div>{t('knowledgeBase.showArticleInOtherLang')}</div>
							<ul className="p-0 m-0">
								{articleQuery.data.alternates.map(
									({ articleId, slug, language: { code, nativeName } }) => {
										const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
											slug ? `-${slugify(slug)}` : ''
										}`;
										return (
											<li key={code}>
												<Link
													to={{
														pathname,
														search: `?lang=${code}`,
													}}
												>
													{nativeName}
												</Link>
											</li>
										);
									}
								)}
							</ul>
						</div>
					</>
				);
			}
		}

		const contentParserOptions = {
			replaces: {
				panelPrefix: panelLinkPrefix,
				organization: organizationName,
				program: programName,
				usertype: panelType,
			},
			disableFormat: true,
		};

		if (articleQuery.data.article) {
			return (
				<>
					<Helmet title={articleQuery.data.articleContent.title} />
					<Box variant="white-bordered">
						<BoxSection header={articleQuery.data.articleContent.title}>
							<ArticlePresenter
								readed={readed}
								article={articleQuery.data.article}
								articleContent={articleQuery.data.articleContent}
								options={{ disableRating }}
								contentParserOptions={contentParserOptions}
								knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
							/>
						</BoxSection>
					</Box>
				</>
			);
		}
	}

	return null;
};
